import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BiBarChartAlt2,
  BiUser,
  BiMessageSquareDetail,
  BiBot,
  BiPlus,
  BiRefresh,
  BiChevronDown,
  BiCheck,
  BiFilter,
  BiStore,
  BiTrendingUp,
  BiTrendingDown,
} from "react-icons/bi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/Dashboard.css";
import ConnectWhatsApp from "../component/ConnectWhatsapp";

const CHART_COLORS = {
  sent: "#4e73df",
  delivered: "#1cc88a",
  read: "#36b9cc",
  failed: "#e74a3b",
  opened: "#f6c23e",
  clicked: "#858796",
};

const formatNumber = (value) =>
  typeof value === "number" ? value.toLocaleString() : value ?? "-";
const safeArray = (value) => (Array.isArray(value) ? value : []);
const formatPhone = (value) => {
  if (!value) return "-";
  const str = String(value);
  return str.startsWith("+") ? str : `+${str}`;
};

const StatCard = ({
  title,
  value,
  icon,
  subtext,
  trend,
  trendValue,
  loading,
  color = "primary",
  tooltip,
}) => (
  <div className="col-xl-3 col-md-6 mb-4">
    <div className="card stat-card h-100">
      <div className="card-body">
        <div className="stat-card-header">
          <div className="stat-label">
            {title}
            {tooltip && (
              <span className="stat-tooltip" title={tooltip}>
                ⓘ
              </span>
            )}
          </div>
          <div className={`stat-icon stat-icon-${color}`}>
            {icon}
          </div>
        </div>
        <div className="stat-value">
          {loading ? (
            <div className="placeholder-glow">
              <span className="placeholder col-6"></span>
            </div>
          ) : (
            formatNumber(value)
          )}
        </div>
        <div className="stat-meta">
          {subtext && <div className="stat-subtext">{subtext}</div>}
          {trend !== undefined && trendValue !== undefined && (
            <div className={`stat-trend ${trend > 0 ? "up" : "down"}`}>
              {trend > 0 ? (
                <BiTrendingUp className="me-1" />
              ) : (
                <BiTrendingDown className="me-1" />
              )}
              <span>
                {trend > 0 ? "+" : ""}
                {trendValue}% vs last period
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Enhanced RecentItem with better status handling
const RecentItem = ({ item, type, onClick }) => {
  const formatDate = (date) => {
    if (!date) return "-";
    const now = new Date();
    const itemDate = new Date(date);
    const diffDays = Math.floor((now - itemDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return itemDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { class: "success", label: "Active" },
      completed: { class: "success", label: "Completed" },
      delivered: { class: "success", label: "Delivered" },
      sent: { class: "info", label: "Sent" },
      scheduled: { class: "warning", label: "Scheduled" },
      processing: { class: "warning", label: "Processing" },
      failed: { class: "danger", label: "Failed" },
      stopped: { class: "danger", label: "Stopped" },
      read: { class: "primary", label: "Read" },
    };

    const config = statusConfig[status?.toLowerCase()] || { class: "secondary", label: status || "Unknown" };
    
    return (
      <span className={`badge bg-${config.class} rounded-pill px-2 py-1`}>
        {config.label}
      </span>
    );
  };

  const getItemIcon = () => {
    switch (type) {
      case "campaign": return <BiBarChartAlt2 className="text-primary" />;
      case "workflow": return <BiBot className="text-success" />;
      case "contact": return <BiUser className="text-info" />;
      default: return <BiBarChartAlt2 className="text-muted" />;
    }
  };

  return (
    <div 
      className="recent-item d-flex justify-content-between align-items-center p-3 border-bottom hover-highlight"
      onClick={onClick ? () => onClick(item) : undefined}
      style={onClick ? { cursor: 'pointer' } : {}}
    >
      <div className="d-flex align-items-center">
        <div className="icon-container me-3">
          {getItemIcon()}
        </div>
        <div className="d-flex flex-column">
          <div className="fw-semibold text-truncate" style={{ maxWidth: '200px' }}>
            {item.name || item.userName || item.phoneNumber || 'Unnamed'}
          </div>
          <div className="small text-muted mt-1">
            {type === 'contact' ? (
              <span>{formatPhone(item.phoneNumber)}</span>
            ) : (
              getStatusBadge(item.status)
            )}
          </div>
        </div>
      </div>
      <div className="text-muted small text-end">
        <div>{formatDate(item.createdAt || item.updatedAt)}</div>
        {type === 'campaign' && item.contacts && (
          <div className="mt-1">{item.contacts.length} contacts</div>
        )}
      </div>
    </div>
  );
};

// Enhanced DateRangeDropdown
const DateRangeDropdown = ({ filter, setFilter, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const options = [
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 Days" },
    { value: "30d", label: "Last 30 Days" },
    { value: "90d", label: "Last 90 Days" },
    { value: "all", label: "All Time" },
  ];

  return (
    <div className="dropdown date-range-dropdown">
      <button
        className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center"
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
      >
        <BiFilter className="me-2" />
        {options.find(opt => opt.value === filter)?.label || "Select Range"}
        <BiChevronDown className={`ms-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="dropdown-menu show">
          {options.map((option) => (
            <button
              key={option.value}
              className={`dropdown-item d-flex justify-content-between align-items-center ${filter === option.value ? 'active' : ''}`}
              onClick={() => {
                setFilter(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
              {filter === option.value && <BiCheck className="text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SectionHeader = ({ title, subtitle, action }) => (
  <div className="section-header">
    <div>
      <h6 className="section-title">{title}</h6>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
    </div>
    {action && <div className="section-actions">{action}</div>}
  </div>
);

const EmptyState = ({ icon, title, description, action }) => (
  <div className="empty-state-block">
    <div className="empty-state-icon">{icon}</div>
    <h6 className="mb-1">{title}</h6>
    <p className="text-muted small mb-3">{description}</p>
    {action}
  </div>
);

// ClientInfoPanel with better connection status
const ClientInfoPanel = ({ tenant_name, tenant_email, connected, connectionInfo }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="card shadow-sm">
      <div 
        className="card-header d-flex justify-content-between align-items-center hover-highlight"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex align-items-center">
          <BiStore className="me-2 text-primary" />
          <div>
            <div className="fw-semibold">{tenant_name}</div>
            <div className="small text-muted">{tenant_email}</div>
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div className={`badge ${connected ? 'bg-success' : 'bg-warning'} me-2`}>
            {connected ? 'Connected' : 'Not Connected'}
          </div>
          <BiChevronDown className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {isExpanded && (
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="small text-muted mb-1">Business Name</label>
                <div className="fw-medium">{tenant_name}</div>
              </div>
              <div className="mb-3">
                <label className="small text-muted mb-1">Account Email</label>
                <div className="fw-medium">{tenant_email}</div>
              </div>
            </div>
            {connected && connectionInfo && (
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="small text-muted mb-1">WhatsApp Number</label>
                  <div className="fw-medium">{connectionInfo.phone_display || 'N/A'}</div>
                </div>
                <div className="mb-3">
                  <label className="small text-muted mb-1">WABA ID</label>
                  <div className="fw-medium small font-monospace">
                    {connectionInfo.whatsapp_waba_id || 'N/A'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip bg-white p-3 shadow rounded border">
        <p className="fw-semibold mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="mb-0" style={{ color: entry.color }}>
            {entry.name}: <span className="fw-semibold">{entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("30d");
  const [connected, setConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const navigate = useNavigate();

  const tenant_name = localStorage.getItem("tenantName");
  const tenant_email = localStorage.getItem("tenantEmail");

  // Memoized calculations
  const messageMetrics = useMemo(() => {
    if (!data?.messages) return null;
    
    const { sent = 0, delivered = 0, read = 0, failed = 0 } = data.messages;
    const total = sent + delivered + read + failed;
    const deliveryRate = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0;
    const readRate = delivered > 0 ? ((read / delivered) * 100).toFixed(1) : 0;
    const successRate = sent > 0 ? (((delivered + read) / sent) * 100).toFixed(1) : 0;
    
    return { total, deliveryRate, readRate, successRate };
  }, [data?.messages]);

  const campaignMetrics = useMemo(() => {
    if (!data?.campaigns) return null;
    
    const { total = 0, active = 0, scheduled = 0, completed = 0 } = data.campaigns;
    const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
    
    return { total, active, scheduled, completed, completionRate };
  }, [data?.campaigns]);

  const rangeLabel = useMemo(() => {
    switch (filter) {
      case "today":
        return "Today";
      case "7d":
        return "Last 7 days";
      case "30d":
        return "Last 30 days";
      case "90d":
        return "Last 90 days";
      default:
        return "All time";
    }
  }, [filter]);

  const recentCampaigns = useMemo(
    () => safeArray(data?.campaigns?.recent).slice(0, 5),
    [data?.campaigns]
  );
  const recentWorkflows = useMemo(
    () => safeArray(data?.workflows?.recent).slice(0, 5),
    [data?.workflows]
  );
  const recentContacts = useMemo(
    () => safeArray(data?.users?.recent).slice(0, 5),
    [data?.users]
  );

  const statCards = useMemo(
    () => [
      {
        title: "Total Campaigns",
        value: campaignMetrics?.total || 0,
        icon: <BiBarChartAlt2 size={20} />,
        subtext: `${campaignMetrics?.active || 0} active • ${campaignMetrics?.scheduled || 0} scheduled`,
        trend: campaignMetrics?.completionRate > 50 ? 1 : -1,
        trendValue: campaignMetrics?.completionRate || 0,
        color: "primary",
        tooltip: "Total campaigns created in selected period",
      },
      {
        title: "Messages Sent",
        value: data?.messages?.sent || 0,
        icon: <BiMessageSquareDetail size={20} />,
        subtext: `${data?.messages?.delivered || 0} delivered • ${data?.messages?.read || 0} read`,
        trend: messageMetrics?.successRate > 90 ? 1 : -1,
        trendValue: messageMetrics?.successRate || 0,
        color: "success",
        tooltip: "Message delivery and engagement metrics",
      },
      {
        title: "Contacts",
        value: data?.users?.total || 0,
        icon: <BiUser size={20} />,
        subtext: `${data?.users?.optedIn || 0} opted in • ${data?.users?.active || 0} active`,
        color: "info",
        tooltip: "Total contacts in your database",
      },
      {
        title: "Workflows",
        value: data?.workflows?.total || 0,
        icon: <BiBot size={20} />,
        subtext: `${data?.workflows?.active || 0} active • ${data?.workflows?.completed || 0} completed`,
        color: "warning",
        tooltip: "Automated workflow processes",
      },
    ],
    [campaignMetrics, data, messageMetrics]
  );

  const insightCards = useMemo(
    () => [
      {
        label: "Delivery rate",
        value: messageMetrics?.deliveryRate ? `${messageMetrics.deliveryRate}%` : "--",
        hint: "Delivered vs sent",
        color: "primary",
      },
      {
        label: "Read rate",
        value: messageMetrics?.readRate ? `${messageMetrics.readRate}%` : "--",
        hint: "Reads vs delivered",
        color: "success",
      },
      {
        label: "Completion",
        value: campaignMetrics?.completionRate ? `${campaignMetrics.completionRate}%` : "--",
        hint: "Campaigns completed",
        color: "warning",
      },
      {
        label: "Connection",
        value: connected ? "Healthy" : "Action needed",
        hint: connected ? "WhatsApp connected" : "Connect WhatsApp",
        color: connected ? "success" : "danger",
      },
    ],
    [campaignMetrics, connected, messageMetrics]
  );

  // Fetch connection status
  const fetchConnectionStatus = useCallback(async () => {
    try {
      if (!tenant_email) return;
      const resp = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/whatsapp/status`,
        { headers: { "x-email": tenant_email } }
      );
      setConnected(!!resp.data?.connected);
      setConnectionInfo(resp.data || null);
    } catch (err) {
      console.error("Error fetching WhatsApp status:", err);
    }
  }, [tenant_email]);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      if (!tenant_email) return;
      setLoading(true);
      setError(null);
      
      const url = `${process.env.REACT_APP_API_URL}/api/admin/dashboard/summary?range=${filter}`;
      const res = await axios.get(url, {
        headers: { "x-email": tenant_email },
        timeout: 10000, // 10 second timeout
      });
      
      setData(res.data);
      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(
        err.response?.data?.message || 
        err.message || 
        "Unable to load dashboard data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [filter, tenant_email]);

  // Initial data fetch
  useEffect(() => {
    if (!tenant_email) return;
    fetchConnectionStatus();
    fetchDashboardData();
  }, [fetchConnectionStatus, fetchDashboardData]);

  // Handle item click
  const handleItemClick = useCallback((item, type) => {
    switch (type) {
      case 'campaign':
        navigate(`/${tenant_name}/campaign/${item._id || item.id}`);
        break;
      case 'workflow':
        navigate(`/${tenant_name}/workflow/${item._id || item.id}`);
        break;
      case 'contact':
        navigate(`/${tenant_name}/contact/${item._id || item.id}`);
        break;
      default:
        break;
    }
  }, [navigate, tenant_name]);

  if (loading && !data) {
    return (
      <div className="container-fluid py-5">
        <div className="d-flex justify-content-center align-items-center min-vh-50">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} />
            <div className="h5 text-gray-800">Loading your dashboard...</div>
            <p className="text-muted small mt-2">Fetching the latest metrics and analytics</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid dashboard-page modern-dashboard">
      <div className="dashboard-shell">
        <div className="dashboard-toolbar">
          <div className="toolbar-left">
            <div className="eyebrow">Overview</div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">
              Track campaign momentum, delivery health, and contact growth in one place.
            </p>
            <div className="toolbar-meta">
              <span className={`status-chip ${connected ? "success" : "warning"}`}>
                {connected ? "WhatsApp Connected" : "WhatsApp Not Connected"}
              </span>
              <span className="status-chip neutral">{rangeLabel}</span>
              {lastUpdatedAt && (
                <span className="status-chip neutral">
                  Updated {lastUpdatedAt.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
          <div className="toolbar-actions">
            <ConnectWhatsApp
              tenant_email={tenant_email}
              connected={connected}
              onConnect={() => {
                fetchConnectionStatus();
                fetchDashboardData();
              }}
            />
            <DateRangeDropdown filter={filter} setFilter={setFilter} loading={loading} />
            <button
              className="btn btn-outline-secondary"
              onClick={fetchDashboardData}
              disabled={loading}
            >
              <BiRefresh className={`me-1 ${loading ? "spin" : ""}`} />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/${tenant_name}/campaign/create`)}
            >
              <BiPlus className="me-1" /> New Campaign
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <strong>Error!</strong> {error}
            <div className="d-flex gap-2 mt-2">
              <button className="btn btn-sm btn-light" onClick={fetchDashboardData}>
                Retry
              </button>
              <button type="button" className="btn-close" onClick={() => setError(null)} />
            </div>
          </div>
        )}

        <div className="insight-strip">
          {insightCards.map((insight) => (
            <div key={insight.label} className="insight-card">
              <div className={`insight-dot ${insight.color}`} />
              <div>
                <div className="insight-label">{insight.label}</div>
                <div className="insight-value">{insight.value}</div>
                <div className="insight-hint">{insight.hint}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="dashboard-grid">
          <section className="dashboard-main">
          <div className="kpi-grid row">
            {statCards.map((card) => (
              <StatCard key={card.title} loading={loading} {...card} />
            ))}
          </div>

          <div className="card section-card">
            <SectionHeader
              title="Messages Performance"
              subtitle="Delivery, read and sent trend"
              action={<span className="text-muted small">{rangeLabel}</span>}
            />
            <div className="card-body">
              <div className="chart-area">
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart
                    data={data?.messages?.trend || []}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="sent"
                      name="Messages Sent"
                      stroke={CHART_COLORS.sent}
                      fill="url(#colorSent)"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="delivered"
                      name="Delivered"
                      stroke={CHART_COLORS.delivered}
                      fill="url(#colorDelivered)"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="read"
                      name="Read"
                      stroke={CHART_COLORS.read}
                      fill="url(#colorRead)"
                      fillOpacity={0.6}
                    />
                    <defs>
                      <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.sent} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={CHART_COLORS.sent} stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.delivered} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={CHART_COLORS.delivered} stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorRead" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={CHART_COLORS.read} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={CHART_COLORS.read} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {messageMetrics && (
            <div className="card section-card">
              <SectionHeader title="Performance Summary" subtitle="Engagement benchmarks" />
              <div className="card-body">
                <div className="summary-grid">
                  <div className="summary-tile">
                    <div className="summary-value text-primary">{messageMetrics.deliveryRate}%</div>
                    <div className="summary-label">Delivery Rate</div>
                  </div>
                  <div className="summary-tile">
                    <div className="summary-value text-success">{messageMetrics.readRate}%</div>
                    <div className="summary-label">Read Rate</div>
                  </div>
                  <div className="summary-tile">
                    <div className="summary-value text-info">
                      {campaignMetrics?.completionRate || 0}%
                    </div>
                    <div className="summary-label">Campaign Completion</div>
                  </div>
                  <div className="summary-tile">
                    <div className="summary-value text-warning">{data?.users?.growth || 0}%</div>
                    <div className="summary-label">Contact Growth</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card section-card">
            <SectionHeader
              title="Recent Campaigns"
              subtitle="Latest activity"
              action={
                <a href={`/${tenant_name}/campaigns`} className="btn btn-sm btn-link">
                  View All →
                </a>
              }
            />
            <div className="card-body p-0">
              {recentCampaigns.length > 0 ? (
                recentCampaigns.map((campaign) => (
                  <RecentItem
                    key={campaign._id}
                    item={campaign}
                    type="campaign"
                    onClick={(item) => handleItemClick(item, "campaign")}
                  />
                ))
              ) : (
                <EmptyState
                  icon={<BiBarChartAlt2 size={36} />}
                  title="No recent campaigns"
                  description="Create your first campaign to start tracking performance."
                  action={
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate(`/${tenant_name}/campaign/create`)}
                    >
                      Create campaign
                    </button>
                  }
                />
              )}
            </div>
          </div>
          </section>

          <aside className="dashboard-side">
          <div className="card section-card">
            <SectionHeader title="Account Status" subtitle={tenant_email} />
            <div className="card-body p-0">
              <ClientInfoPanel
                tenant_name={tenant_name}
                tenant_email={tenant_email}
                connected={connected}
                connectionInfo={connectionInfo}
              />
            </div>
          </div>

          <div className="card section-card">
            <SectionHeader title="Delivery Status" subtitle="Realtime breakdown" />
            <div className="card-body">
              <div className="chart-pie pt-2">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Delivered", value: data?.messages?.delivered || 0 },
                        { name: "Read", value: data?.messages?.read || 0 },
                        { name: "Failed", value: data?.messages?.failed || 0 },
                        {
                          name: "Sent",
                          value: Math.max(
                            0,
                            (data?.messages?.sent || 0) -
                              (data?.messages?.delivered || 0)
                          ),
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={78}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {[CHART_COLORS.delivered, CHART_COLORS.read, CHART_COLORS.failed, CHART_COLORS.sent].map(
                        (color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        )
                      )}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} messages`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="status-legend">
                <span><i className="legend-dot bg-success" /> Delivered</span>
                <span><i className="legend-dot bg-info" /> Read</span>
                <span><i className="legend-dot bg-danger" /> Failed</span>
                <span><i className="legend-dot bg-primary" /> Sent</span>
              </div>
            </div>
          </div>

          <div className="card section-card">
            <SectionHeader
              title="Recent Workflows"
              action={
                <a href={`/${tenant_name}/Bot-Workflow-manage`} className="btn btn-sm btn-link">
                  View All →
                </a>
              }
            />
            <div className="card-body p-0">
              {recentWorkflows.length > 0 ? (
                recentWorkflows.map((workflow) => (
                  <RecentItem
                    key={workflow._id}
                    item={workflow}
                    type="workflow"
                    onClick={(item) => handleItemClick(item, "workflow")}
                  />
                ))
              ) : (
                <EmptyState
                  icon={<BiBot size={36} />}
                  title="No recent workflows"
                  description="Build workflows to automate customer journeys."
                />
              )}
            </div>
          </div>

          <div className="card section-card">
            <SectionHeader
              title="Recent Contacts"
              action={
                <a href={`/${tenant_name}/users`} className="btn btn-sm btn-link">
                  View All →
                </a>
              }
            />
            <div className="card-body p-0">
              {recentContacts.length > 0 ? (
                recentContacts.map((user) => (
                  <RecentItem
                    key={user._id}
                    item={user}
                    type="contact"
                    onClick={(item) => handleItemClick(item, "contact")}
                  />
                ))
              ) : (
                <EmptyState
                  icon={<BiUser size={36} />}
                  title="No recent contacts"
                  description="Import contacts to start messaging."
                />
              )}
            </div>
          </div>

          <div className="card section-card cta-card">
            <div className="cta-title">Ready to boost engagement?</div>
            <p className="cta-subtitle">
              Launch targeted campaigns and automate workflows in minutes.
            </p>
            <div className="cta-actions">
              <button
                className="btn btn-light"
                onClick={() => navigate(`/${tenant_name}/campaign/create`)}
              >
                <BiPlus className="me-1" /> Create Campaign
              </button>
              <button
                className="btn btn-outline-light"
                onClick={() => navigate(`/${tenant_name}/Bot-Workflow-manage`)}
              >
                Manage Workflows
              </button>
            </div>
          </div>
          </aside>
        </div>
      </div>
    </div>
  );
}